import type { RenewalStartFrom, SubscriptionState } from "./types";

/**
 * Preview of what a renewal will do, computed client-side so the operator can
 * see the resulting term before committing.
 *
 * This MIRRORS `classifyRenewal` / `computeRenewalTerm` in the backend's
 * `helper/subscription-renewal.js` (CTX-23, CTX-522). The backend stays
 * authoritative — it recomputes everything inside the transaction and its
 * result is what gets persisted. Keep the two in sync: if the backend's timing
 * rules change, this preview has to follow, or the UI will promise a term the
 * API won't create.
 */

/** How the new term relates to the current one. Same taxonomy as the backend. */
export type RenewalType = "early" | "same_day" | "after_expiry";

export interface RenewalPreview {
	type: RenewalType;
	/**
	 * Anchor as requested, echoed back the way the backend records it — an
	 * early or same-day renewal reports what was asked for even though it
	 * always chains.
	 */
	startFrom: RenewalStartFrom;
	/** Start of the new term. */
	newStart: Date;
	/** End of the new term: `newStart + termMonths`, minus a day so terms don't overlap. */
	newEnd: Date;
	/** State the new subscription is born in. */
	newState: Extract<SubscriptionState, "active" | "future" | "expired">;
	/** Service-level validity once the renewal lands. */
	newEffectiveEnd: Date;
	/**
	 * Stretch the service is left without any subscription covering it, or
	 * null when the new term is contiguous with the current one. Only an
	 * after-expiry renewal anchored to today can produce one.
	 */
	gap: { from: Date; to: Date; days: number } | null;
	/**
	 * The term would close before today, so the backend refuses it. Reachable
	 * only by anchoring to the end of a term that lapsed long enough ago for
	 * the whole new term to fit in the past.
	 */
	termAlreadyEnded: boolean;
}

const MS_PER_DAY = 86_400_000;

function addMonths(date: Date, months: number): Date {
	const result = new Date(date);
	result.setMonth(result.getMonth() + months);
	return result;
}

function addDays(date: Date, days: number): Date {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
}

/**
 * Midnight of the given date. Contract boundaries are calendar days, and
 * `parseSubscriptionDate` already anchors them to LOCAL midnight, so the whole
 * preview stays in local time — the backend does the same arithmetic in UTC
 * over the same calendar days.
 */
function startOfDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole calendar days from `from` to `to`; negative once `to` is earlier. */
function dayDiff(to: Date, from: Date): number {
	return Math.round(
		(startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY,
	);
}

/** Calendar-day comparison, ignoring time of day. */
function isSameCalendarDay(a: Date, b: Date): boolean {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

export interface RenewalPreviewInput {
	/** Current term end. Null for an open/legacy term — treated as renewing from today. */
	contractEnd: Date | null;
	termMonths: number;
	now: Date;
	/** Defaults to `"today"`, which is how every renewal behaved before CTX-522. */
	startFrom?: RenewalStartFrom;
}

/**
 * Classify and project the renewal.
 *
 * - **early** (`now < contractEnd`): the new term starts the day after the
 *   current one ends, so the service's coverage extends forward with no gap
 *   and no overlap. The current subscription becomes `renewed`, the new one is
 *   born `future`.
 * - **same_day** (`now` falls on the end date): the new term starts the day
 *   after, because the current term still covers the rest of today.
 * - **after_expiry** (`now > contractEnd`): the subscription already lapsed.
 *   The new term starts today, leaving the uncovered stretch as history —
 *   unless `startFrom` is `"term_end"`, which opens it the day the old term
 *   closed and so closes the gap.
 *
 * `startFrom` is ignored for early and same-day renewals: honouring `"today"`
 * there would open a term while the current one is still running and put two
 * subscriptions on the same day.
 */
export function computeRenewalPreview({
	contractEnd,
	termMonths,
	now,
	startFrom = "today",
}: RenewalPreviewInput): RenewalPreview {
	const months = Math.max(1, Math.trunc(termMonths));

	let type: RenewalType;

	if (!contractEnd) {
		type = "same_day";
	} else if (isSameCalendarDay(now, contractEnd)) {
		type = "same_day";
	} else if (now < contractEnd) {
		type = "early";
	} else {
		type = "after_expiry";
	}

	// Terms are contiguous, never intersecting: the successor opens the day
	// after the current one closes. An after-expiry renewal starts today
	// instead, because that gap is real — unless the operator anchors it back
	// to the term end. Same when there is no known end to chain from.
	const chainsFromCurrentTerm =
		contractEnd !== null &&
		(type !== "after_expiry" || startFrom === "term_end");
	const newStart = chainsFromCurrentTerm ? addDays(contractEnd, 1) : now;
	const newEnd = addDays(addMonths(newStart, months), -1);

	// Read off the term's own dates rather than off how it was derived: a term
	// anchored to a lapsed term's end starts in the past, so "chains, therefore
	// future" no longer holds, and a short enough one has already closed.
	let newState: RenewalPreview["newState"];
	if (dayDiff(newEnd, now) < 0) {
		newState = "expired";
	} else if (dayDiff(newStart, now) > 0) {
		newState = "future";
	} else {
		newState = "active";
	}

	// The uncovered stretch runs from the day after the old term closed to the
	// day before the new one opens.
	let gap: RenewalPreview["gap"] = null;
	if (contractEnd) {
		const gapFrom = addDays(contractEnd, 1);
		const days = dayDiff(newStart, gapFrom);
		if (days > 0) {
			gap = { from: gapFrom, to: addDays(newStart, -1), days };
		}
	}

	return {
		type,
		startFrom,
		newStart,
		newEnd,
		newState,
		newEffectiveEnd: newEnd,
		gap,
		termAlreadyEnded: newState === "expired",
	};
}

/** Label for the renewal-type badge. */
export const RENEWAL_TYPE_LABEL: Record<RenewalType, string> = {
	early: "Early renewal",
	same_day: "Same-day renewal",
	after_expiry: "Renewed after expiry",
};
